import { useGetSubscription } from "@/hooks/useSubscription"
import { MembershipRoles } from "@/types/User"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { FC, useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useApp } from "../../AppProvider"
import { PAGE_SIZE } from "../../constants"
import { useGetLabReports } from "../../hooks/useLabReports"
import { usePaginationState } from "../../hooks/usePaginationState"
import { LabReport } from "../../types/LabReport"
import { classNames } from "../../utils/classNames"
import { FilterConfig, filterData, getFilterOptions } from "../../utils/data"
import { Pagination } from "../common"
import { ErrorMessage } from "../common/Feedback/ErrorMessage"
import { LoadingMessage } from "../common/Feedback/LoadingMessage"
import { Filters, VariableFilter } from "../common/Filters/Filters"
import { Search } from "../common/Search"
import { StatusBadge } from "../common/StatusBadge"
import { IndeterminateCheckbox } from "../common/Tables/IndeterminateCheckbox"
import { fuzzyFilter } from "../common/Tables/utils"

interface LabReportsTableProps {}

const columnHelper = createColumnHelper<LabReport>()
const columns = [
  {
    id: "select",
    header: ({ table }: any) => (
      <div className="-mr-5 pb-px pl-1">
        <IndeterminateCheckbox
          className="checkbox"
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      </div>
    ),
    cell: ({ row }: any) => (
      <div className="-mr-5 pb-px pl-1">
        <IndeterminateCheckbox
          className="checkbox"
          {...{
            checked: row.getIsSelected(),
            disabled: !row.getCanSelect(),
            indeterminate: row.getIsSomeSelected(),
            onChange: row.getToggleSelectedHandler(),
          }}
        />
      </div>
    ),
  },
  columnHelper.accessor("patient.name", {
    header: "Patient",
    cell: (info) => <span className="cursor-pointer font-semibold text-gray-900">{info.getValue()}</span>,
  }),
  columnHelper.accessor("description", {
    header: "Test Type",
    cell: (info) => <span className="block max-w-[35ch] truncate">{info.getValue()}</span>,
  }),
  columnHelper.accessor("doctorName", {
    header: "Doctor",
  }),
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => <StatusBadge status={info.getValue()} />,
  }),
  columnHelper.accessor("resultsUpdatedAt", {
    header: "Reported At",
    cell: (info) => (
      <span className="block max-w-[35ch] truncate">
        {info.getValue()
          ? new Intl.DateTimeFormat("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric",
            }).format(new Date(info.getValue()))
          : null}
      </span>
    ),
  }),
  columnHelper.display({
    id: "actions",
    cell: (props: any) => {
      return (
        <span className="flex items-center justify-end gap-x-2">
          <span className="text-primary-600 hover:text-primary-900 text-sm font-medium">Details</span>
        </span>
      )
    },
  }),
]

export const LabReportsTable: FC<LabReportsTableProps> = () => {
  // General
  const { token, membership } = useApp()
  const navigate = useNavigate()
  const { organizationId } = useParams()

  const subQuery = useGetSubscription(token, organizationId)

  const { setPage, paginationState } = usePaginationState({
    paramName: "labReportsPage",
    pageSize: PAGE_SIZE,
  })

  // Table Data
  const labsQuery = useGetLabReports(organizationId!, token)
  const data = useMemo(() => labsQuery?.data?.data ?? [], [labsQuery?.data])
  const [filters, setFilters] = useState<VariableFilter[]>([])
  const filterConfigs: FilterConfig[] = [
    { key: "source", title: "Source" },
    { key: "status", title: "Status" },
    { key: "doctorName", title: "Doctor" },
    { key: "description", title: "Test type" },
  ]
  const filterOptions = getFilterOptions(data, filterConfigs)
  const [filteredData, setFilteredData] = useState(data)
  const [search, setSearch] = useState<string>("")

  useEffect(() => {
    setFilteredData(filterData(data, filters))
  }, [data, filters])

  const errors = labsQuery?.data?.errors ?? []
  const table = useReactTable({
    data: filteredData,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      globalFilter: search,
      pagination: paginationState,
    },
    onGlobalFilterChange: setSearch,
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newState = updater(paginationState)
        setPage(newState.pageIndex + 1)
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  return (
    <div className="card">
      <div className="mt-2 flex items-stretch justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Lab Reports</h2>
        <div className="flex items-stretch gap-x-2">
          <Search value={search} onChange={setSearch} />
          <Filters
            viewName="labReports"
            filters={filters}
            setFilters={setFilters}
            filterOptions={filterOptions}
          />
        </div>
      </div>

      {/* Display warning if subscription is inactive or missing */}
      {!!subQuery.data && subQuery.data?.subscription?.status !== "active" ? (
        <ErrorMessage
          message={
            membership?.role === MembershipRoles.Admin ? (
              <>
                Please
                <Link className="underline" to={`/organizations/${organizationId}/settings/billing`}>
                  click here
                </Link>
                to update your subscription to download lab reports.
              </>
            ) : (
              <>
                Your organization does not have an active subscription. Please contact your administrator to
                update the subscription.
              </>
            )
          }
        />
      ) : null}

      {labsQuery.isLoading ? (
        <LoadingMessage />
      ) : labsQuery.isError ? (
        <ErrorMessage message={labsQuery?.error?.message ?? "Unknown application error"} />
      ) : (data && data.length > 0) || errors.length > 0 ? (
        <>
          {errors.length > 0 ? (
            <>
              {errors.map((error, i) => (
                <ErrorMessage key={i} message={error} />
              ))}
            </>
          ) : null}
          <div className="mt-4 flow-root overflow-hidden">
            <div className="3xl:-mx-8 3xl:max-h-full -mx-4 -my-2 max-h-[500px] overflow-x-auto sm:-mx-6">
              <div className="3xl:px-8 inline-block min-w-full py-2 align-middle sm:px-6">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header, i) => (
                          <th
                            scope="col"
                            className={classNames(
                              "relative py-3.5 text-left text-sm font-semibold text-gray-900",
                              i === 0
                                ? "pr-3 pl-4 sm:pl-0"
                                : i === headerGroup.headers.length - 1
                                  ? "pr-4 pl-3 sm:pr-0"
                                  : "px-3",
                            )}
                            key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {table.getRowModel().rows.length > 0 ? (
                      <>
                        {table.getRowModel().rows.map((row) => (
                          <tr
                            key={row.id}
                            className={classNames(
                              "cursor-pointer hover:bg-gray-50",
                              row.getIsSelected() ? "bg-gray-100" : "bg-white",
                            )}
                            onClick={() => {
                              // Navigate to the patient details page when the row is clicked
                              if (row.original.patient?._id) {
                                navigate(
                                  `/organizations/${organizationId}/patients/${row.original.patient._id}`,
                                )
                              } else {
                                navigate(`/organizations/${organizationId}/lab-reports/${row.original.id}`)
                              }
                            }}>
                            {row.getVisibleCells().map((cell, i) => (
                              <td
                                className={classNames(
                                  "font-regular py-4 text-sm whitespace-nowrap text-gray-500",
                                  i === 0
                                    ? "pr-3 pl-4 sm:pl-0"
                                    : i === row.getVisibleCells().length - 1
                                      ? "pr-4 pl-3 sm:pr-0"
                                      : "px-3",
                                )}
                                key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, {
                                  ...cell.getContext(),
                                  navigate,
                                })}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td
                          className="font-regular px-4 py-4 text-center text-sm whitespace-nowrap text-gray-500"
                          colSpan={table.getHeaderGroups()?.[0]?.headers?.length || 100}>
                          No lab reports found for the given filters
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div>
            <Pagination
              page={table.getState().pagination.pageIndex + 1}
              pageCount={table.getPageCount()}
              hasPrevious={table.getCanPreviousPage()}
              onPrevious={() => table.previousPage()}
              hasNext={table.getCanNextPage()}
              onNext={() => table.nextPage()}
              setPage={(i) => table.setPageIndex(i - 1)}
            />
          </div>{" "}
        </>
      ) : (
        <div className="mt-4 text-gray-600">No results found</div>
      )}
    </div>
  )
}
